import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    PermissionsModuleAccessLevel,
    UserAccessService,
    UserModuleAccessCollection,
    ModuleUserAccessMap
} from '@predictiveindex/client-permissions-lib';
import { SessionStorageService } from '../../cache/SessionStorage.service';
import { oneSecondMilli, PI_VERSION } from '../../constants-types/constants';
import { RoleType } from '../../constants-types/Enums';
import { PI } from '../../constants-types/PI.model';
import { ModuleName } from '../../core-data-model';
import { AssessmentAdministrator } from '../../core-data-model/AssessmentAdministrator.model';
import {
    companySettings,
    companySettingsResponse
} from '../../core-data-model/CompanySettings.model';
import {
    ExperimentStatusDictionary,
    ExperimentType
} from '../../core-data-model/ExperimentAccess.model';
import {
    AccountConfig,
    InitData,
    CompanyEmailDomains,
    PendoMetadata,
    UserProfile
} from '../../core-data-model/InitData.model';
import { ConfigurationService } from '../../system-configuration/Configuration.service';
import { ApiService } from '../../system-services/Api.service';
import { ExperimentAccessTransformers } from '../../system-services/ExperimentAccess/ExperimentAccess.datatransform';
import { ExperimentNamesWithUserAttributesServer } from '../../system-services/ExperimentAccess/ExperimentAccess.serverinterface';
import { Logger } from '../Logger';
import { InitDataTransformers } from './InitData.datatransform';
import { InitDataServer } from './InitData.serverinterface';

/*
This is an injectable service that serves as a wrapper around synchronously
loaded Initialisation Data. This is intended to read from an object loaded via
APP_INITIALIZER before the application finishes the bootstrapping process.
*/

@Injectable({ providedIn: 'root' })
export class InitDataService {
    javaScriptInitData: InitData;
    key = 'JavaScriptInitData';
    experimentStatusDict: ExperimentStatusDictionary = {};
    userAccessLevelMap: Map<string, ModuleUserAccessMap>;
    userCompanyPermissionCollection: UserModuleAccessCollection;
    companySettings: companySettings;
    companyEmailDomains: CompanyEmailDomains;

    constructor(
        private config: ConfigurationService,
        private api: ApiService,
        private httpClient: HttpClient,
        private transForm: InitDataTransformers,
        private session: SessionStorageService,
        private experimentTransform: ExperimentAccessTransformers,
        private userAccessService: UserAccessService,
        private logger: Logger
    ) {}

    get isInitDataLoaded(): boolean {
        return (
            this.javaScriptInitData !== null &&
            this.javaScriptInitData !== undefined
        );
    }

    get user(): AssessmentAdministrator {
        return this.javaScriptInitData.administrator;
    }

    get account(): AccountConfig {
        return this.javaScriptInitData.accountConfig;
    }

    get accessibleFeatures(): string[] {
        return this.javaScriptInitData.accessibleFeaturesList;
    }

    get profile(): UserProfile {
        return this.javaScriptInitData.userProfile;
    }

    // Some variables to consolidate the state of digital learning/top nav changes
    get showDigitalLearning(): boolean {
        return this.config.isEnglish();
    }

    get stopNavIsReady(): boolean {
        return false;
    }

    get pendoMetadata(): PendoMetadata {
        return {
            visitor: {
                id: this.javaScriptInitData.administrator.apiUserId,
                loggedInCompanyId:
                    this.javaScriptInitData.userProfile.settings.currentCompany,
                isBornInPI2: this.javaScriptInitData.accountConfig.isBornInPI2,
                isLoggedIntoDefaultCompany:
                    this.javaScriptInitData.userProfile.settings
                        .currentCompany ===
                    this.javaScriptInitData.administrator.companyId,
                // Note: inverse to previous value but name confusion for PMs
                isThirdPartyUser:
                    this.javaScriptInitData.userProfile.settings
                        .currentCompany ===
                    this.javaScriptInitData.administrator.companyId,
                email: this.javaScriptInitData.administrator.email,
                firstName: this.javaScriptInitData.userProfile.firstName,
                lastName: this.javaScriptInitData.userProfile.lastName,
                role: this.javaScriptInitData.administrator.roleName,
                languageLocale: PI.languageLocale,
                softwareVersion: PI_VERSION,
                defaultCompanyId:
                    this.javaScriptInitData.administrator.apiCompanyId,
                browserWidth: window.innerWidth,
                browserHeight: window.innerHeight,
                browserResolution: `${window.innerWidth} x ${window.innerHeight}`,
                partnerCompanyName: this.account.partnerCompanyName,
                partnerContactFirstName: this.account.partnerContactFirstName,
                partnerContactLastName: this.account.partnerContactLastName,
                partnerContactEmail: this.account.partnerContactEmail,
                partnerContactPhone: this.account.partnerContactPhone
            },
            account: {
                id: this.javaScriptInitData.accountConfig.currentApiCompanyId,
                name: this.javaScriptInitData.administrator.company,
                accountStatus:
                    this.javaScriptInitData.accountConfig.accountStatus,
                pi2UpgradeStatus:
                    this.javaScriptInitData.accountConfig.pi2UpgradeStatus,
                piVersion: this.javaScriptInitData.accountConfig.piVersion,
                isBornInPI2: this.javaScriptInitData.accountConfig.isBornInPI2
            }
        };
    }

    // Called by App.ts via the APP_INITIALIZER functionality
    // This promise MUST resolve successfully before the app bootstraps
    // NOTE: this useNewRelic boolean is a temporary fix used to get
    // around an issue the free app is having with newrelic.
    async getJavaScriptInitData(clearCache = false) {
        let response: InitDataServer;
        response = this.session.getItem<InitDataServer>(this.key);

        if (!response || clearCache) {
            [response] = await this.api.get<InitDataServer>(
                `/api/v3/JavaScriptInitData`
            );
            this.session.setItem<InitDataServer>(
                this.key,
                response,
                5 + oneSecondMilli
            );
        }
        this.javaScriptInitData =
            this.transForm.transformServerToClient(response);

        // retrieve experiment data after javascriptInitData
        // so that companyId and userId are available
        // Disabled as of March 2022; for more detail: https://predictiveindex.atlassian.net/browse/PI2-12920
        // await this.getExperimentAccessData();

        // Retrieve partner data for feature flag (and Pendo) usage
        // Remove this call until we can create a way to do so without blowing through sales force API limits.
        // await this.getPartnerData();

        // Retrieve company domain data
        this.getCompanySettings();

        // Retrieve user permissions data
        await this.refreshUserPermissions();

        return this.javaScriptInitData;
    }

    async refreshUserPermissions(): Promise<void> {
        // Retrieve user permission collections including user access level mapping.
        this.userCompanyPermissionCollection =
            await this.getUserPermissionsCollection();
        // Get User Module Access Mapping with collection
        this.userAccessLevelMap = this.getUserAccessLevelMap(
            this.userCompanyPermissionCollection
        );
    }

    async getExperimentAccessData() {
        const experimentNames: string[] = [];

        for (const [_, value] of Object.entries(ExperimentType)) {
            experimentNames.push(value);
        }

        const payload: ExperimentNamesWithUserAttributesServer = {
            userId: this.javaScriptInitData.userProfile.userGuid,
            companyId:
                this.javaScriptInitData.userProfile.settings.currentCompany,
            languageCode: PI.languageLocale,
            experimentNames: experimentNames
        };

        const accessResult: ExperimentStatusDictionary = await this.api
            .post<ExperimentNamesWithUserAttributesServer>(
                `/api/v4/experiments`,
                payload
            )
            .then(([server, response]) => {
                if (response.ok) {
                    return this.experimentTransform.transformServerToClient(
                        server
                    );
                } else {
                    return null;
                }
            })
            .catch((err) => null);

        if (accessResult) {
            this.experimentStatusDict = accessResult;
        }
    }

    hasExperimentAccess(experiment: ExperimentType): boolean {
        return true;
        // Disabled as of March 2022; for more detail: https://predictiveindex.atlassian.net/browse/PI2-12920
        // if (!(experiment in this.experimentStatusDict)) {
        //     return false;
        // } else {
        //     return this.experimentStatusDict[experiment];
        // }
    }

    async getPartnerData() {
        const companyId =
            this.javaScriptInitData.userProfile.settings.currentCompany;
        try {
            const [data] = await this.api.get<{
                forcePartnerName: string;
                forcePartnerContactName: string;
                forcePartnerContactEmail: string;
                forcePartnerContactPhone: string;
                forcePartnerIsDirect: boolean;
            }>(`/api/v4/companies/${companyId}/companysettings/partnerinfo`);
            this.account.partnerCompanyName = data.forcePartnerName;
            this.account.partnerContactFirstName =
                data.forcePartnerContactName.split(' ')[0];
            this.account.partnerContactLastName =
                data.forcePartnerContactName.split(' ')[1];
            this.account.partnerContactEmail = data.forcePartnerContactEmail;
            this.account.partnerContactPhone = data.forcePartnerContactPhone;
            this.account.isDirectClient = data.forcePartnerIsDirect;
        } catch (err) {
            this.account.partnerContactEmail = null;
            this.account.partnerContactPhone = null;
            this.account.isDirectClient = null;
        }
    }

    async getUserPermissionsCollection() {
        const userId = this.javaScriptInitData.userProfile.userGuid;
        const companyId =
            this.javaScriptInitData.userProfile.settings.currentCompany;

        const collection =
            await this.userAccessService.getUserPermissionsCollection(
                companyId,
                userId
            );
        return collection;
    }

    getUserAccessLevelMap(
        c: UserModuleAccessCollection
    ): Map<string, ModuleUserAccessMap> {
        const map = new Map<string, ModuleUserAccessMap>();
        if (!c || !c.accessLevel) {
            return map;
        }
        const keys = Object.keys(c.accessLevel);
        keys.forEach((x) => {
            map.set(x, {
                userAccessLevel:
                    c.accessLevel[x as ModuleName] ??
                    PermissionsModuleAccessLevel.Unknown,
                allowVMAccess: !c.vmAccessLevel
                    ? false
                    : (c.vmAccessLevel[x as ModuleName] ?? false)
            } as ModuleUserAccessMap);
        });
        return map;
    }

    async getCompanySettings() {
        const companyId =
            this.javaScriptInitData.userProfile.settings.currentCompany;
        const emptyCompanyEmailDomain = { allowList: [] };
        const companySettingApiUrl = this.getCompanySettingsApiUrl();
        if (!companySettingApiUrl) {
            return emptyCompanyEmailDomain;
        }

        /**
         * We are hitting the new company settings endpoint here just for domains for now
         * eventually we will want to move all company settings to this endpoint
         */
        this.httpClient
            .get<companySettingsResponse>(
                `${companySettingApiUrl}/api/v5/companies/${companyId}/companysettings`
            )
            .subscribe((response) => {
                if (!response.isSuccess) {
                    this.logger.logError(
                        'Error occured retriveing company settings',
                        companyId
                    );
                    this.companyEmailDomains = emptyCompanyEmailDomain;
                }
                this.companySettings = response.data;
                this.companyEmailDomains = {
                    allowList: response.data.domainAllowList,
                    isAllowNonDomainEnabled:
                        response.data.isAllowNonDomainEnabled
                };
            });
    }

    /**
     * Updates the company domain list
     *
     * @param domains - List of domain names
     */
    updateCompanyDomainList(domains: string[]): void {
        this.companyEmailDomains = {
            allowList: domains
        };
    }

    /**
     * Updates the company settings' employeeAccessRulesEnabled value
     * @param enabled - value of the setting
     **/
    updateEmployeeAccessRuleSetting(enabled: boolean): void {
        this.companySettings.employeeAccessRulesEnabled = enabled;
    }

    getAccountManagementApiUrl(): string {
        return PI.appSettings.accountManagementApiUrl;
    }

    getCompanySettingsApiUrl(): string {
        return PI.appSettings.companySettingsApiUrl;
    }

    getDemoAccountsApiUrl(): string {
        return PI.appSettings.demoAccountsApiUrl;
    }

    hasAccess(featureKey: string): boolean {
        return this.accessibleFeatures.includes(featureKey);
    }

    isAdmin(): boolean {
        return this.user.roleType >= RoleType.AccountAdmin;
    }

    isAccountOwner(): boolean {
        return this.user.roleType >= RoleType.AccountOwner;
    }

    isPI2OrgAdmin(): boolean {
        return this.userCompanyPermissionCollection.isCompanyAdmin;
    }

    isPowerUser(): boolean {
        return this.user.roleType === RoleType.PowerUser;
    }

    isUser(): boolean {
        return this.user.roleType === RoleType.User;
    }

    isReadOnly(): boolean {
        return this.user.roleType === RoleType.ReadOnly;
    }

    isAbacUser(): boolean {
        return this.user.roleType === RoleType.AbacUser;
    }

    isSuperAdmin(): boolean {
        return this.user.roleType === RoleType.SuperAdmin;
    }

    // This only applies to 1.0 users, all 2.0 users are created as AccountAdmins in this context
    hasPermissionLevelOrAbove(roleType: RoleType) {
        return this.user.roleType >= roleType;
    }
}
